type Location = {
  latitude: number;
  longitude: number;
};

type LocationResult = {
  location: Location;
  isFallback: boolean;
};

const options: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 60000,
  maximumAge: 0,
};

export const getUserLocation = async (
  defaultLocation: Location,
): Promise<LocationResult> => {
  if (!navigator.geolocation) {
    // Geolocation not supported. Using default location.
    return { location: defaultLocation, isFallback: true };
  }

  try {
    const permissionStatus = await navigator.permissions.query({
      name: "geolocation",
    });
    if (permissionStatus.state === "denied") {
      // Geolocation permission denied. Using fallback.
      return { location: defaultLocation, isFallback: true };
    }

    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Geolocation permission granted. Using real location.
          resolve({
            location: {
              latitude: Number(position.coords.latitude.toFixed(3)),
              longitude: Number(position.coords.longitude.toFixed(3)),
            },
            isFallback: false,
          });
        },
        (_) => {
          // Error getting location. Using fallback.
          resolve({ location: defaultLocation, isFallback: true });
        },
        options,
      );
    });
  } catch {
    // Unexpected permission error. Using fallback.
    return { location: defaultLocation, isFallback: true };
  }
};

export const sanitizeAddressPart = (value: unknown): string => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";
  const lowered = normalized.toLowerCase();
  return lowered === "null" || lowered === "undefined" ? "" : normalized;
};
